import React, { useMemo, useState } from 'react';
import { Box, Paper, Stack, useMediaQuery, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import ByzMuiDataGrid from 'src/components/core-components/mui/data-grid';
import ByzDataGridToolbar from 'src/components/core-components/mui/data-grid/toolbar';
import PatientDatas from 'src/modules/medcard/patient-datas';
import OpenComponentDialog from 'src/components/shared-components/modal/openComponentModal';
import ByzNurseAddButton from 'src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseAddButton';
import ByzNursePrintButton from 'src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton';
import { useTranslate } from 'src/locales';

interface BaseMedCardListProps {
  title: string;
  columns: GridColDef[];
  rows: any[];
  CreateComponent: React.ComponentType<any>;
  createComponentProps?: any;
  onAdd?: () => void;
  onPrint?: () => void;
  onModalClose?: () => void;
  hidePatientDatas?: boolean;
  pageSize?: number;
  customToolbar?: React.ComponentType<any>;
  extraActions?: React.ReactNode;
  children?: React.ReactNode;
  loading?: boolean;
}

const BaseMedCardList: React.FC<BaseMedCardListProps> = ({
  title,
  columns,
  rows,
  CreateComponent,
  createComponentProps = {},
  onAdd,
  onPrint,
  onModalClose,
  hidePatientDatas = false,
  pageSize = 10,
  customToolbar = ByzDataGridToolbar,
  extraActions,
  children,
  loading
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslate();
  const theme = useTheme();
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const CustomToolbar = () => (
    <ByzDataGridToolbar containerSx={{ pl: 0 }} toolbarSx={{ pl: 0 }} />
  );

  const responsiveColumns = useMemo(
    () =>
      columns.map((column) => {
        if (!isCompactScreen) return column;

        const headerText = String(column.headerName ?? '');
        const normalizedField = String(column.field ?? '').toLowerCase();
        const normalizedHeader = headerText.toLowerCase();
        const isActionColumn =
          normalizedField.includes('edit') ||
          normalizedField.includes('delete') ||
          normalizedField.includes('update') ||
          normalizedField.includes('action') ||
          normalizedHeader.includes('düzenle') ||
          normalizedHeader.includes('duzenle') ||
          normalizedHeader.includes('güncelle') ||
          normalizedHeader.includes('guncelle') ||
          normalizedHeader.includes('sil');

        const targetWidth = isActionColumn
          ? 160
          : headerText.length >= 16
            ? 220
            : headerText.length >= 10
              ? 200
              : 180;

        return {
          ...column,
          flex: undefined,
          minWidth: Math.max(column.minWidth ?? 0, targetWidth),
          width: Math.max(column.width ?? 0, targetWidth),
        };
      }),
    [columns, isCompactScreen]
  );

  const handleAdd = () => {
    if (onAdd) {
      onAdd();
    } else {
      setOpen(true);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    if (onModalClose) {
      onModalClose();
    }
  };

  return (
    <>
      {!hidePatientDatas && <PatientDatas />}

      <Stack mt={5} direction="row" spacing={2}>
        <ByzNurseAddButton onClick={handleAdd} />
        {extraActions}
      </Stack>

      <Box>
        <Paper
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            pb: { xs: 2, sm: 3, md: 4 },
            my: 3,
            borderRadius: "16px",
            overflowX: "auto",
          }}
        >
          <ByzMuiDataGrid
            sx={{
              mb: 4,
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: 1.25,
              },
            }}
            customToolbar={CustomToolbar}
            columns={responsiveColumns}
            pageSize={pageSize}
            rows={rows}
            whiteGrid={true}
            loading={loading}
          />

        </Paper>
      </Box>

      {children}

      <OpenComponentDialog
        title={title}
        open={open}
        onClose={handleModalClose}
        isMedCard={true}
      >
        <CreateComponent
          {...createComponentProps}
          onCloseModal={handleModalClose}
        />
      </OpenComponentDialog>
    </>
  );
};

export default BaseMedCardList;
